import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAdminPageTitle } from "@/hooks/useAdminPageTitle";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";

type AuthMode = "login" | "register" | "forgot" | "update-password";

export default function AdminLoginPage() {
  useAdminPageTitle("Login");

  const { login, register, logout, resetPassword, updatePassword, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectPath = searchParams.get("redirect") || "/dashboard";

  const [mode, setMode] = useState<AuthMode>(() => {
    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("access_token")) return "update-password";
    if (searchParams.get("mode") === "update-password") return "update-password";
    return "login";
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);

  const switchMode = (newMode: AuthMode) => {
    setMode(newMode);
    setError(null);
    setSuccess(null);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await login(email.trim(), password);
      // login() sets user synchronously — navigate immediately
      navigate(redirectPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(email.trim(), password);
      setSuccess("Account created! Please check your email to confirm, then sign in.");
      setMode("login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!email.trim()) {
      setError("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email.trim());
      setSuccess("Password reset link sent! Check your email inbox.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send reset link.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!newPassword.trim()) {
      setError("Please enter a new password.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await updatePassword(newPassword);
      setSuccess("Password updated! Redirecting you to sign in...");
      setTimeout(() => {
        window.location.hash = "";
        setSearchParams({});
        logout();
        setMode("login");
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update password.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error: oAuthErr } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}${redirectPath}`,
        },
      });
      if (oAuthErr) throw oAuthErr;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Google sign-in failed");
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl border border-gray-200 w-full max-w-md p-8 shadow-sm">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img
            src="https://storage.readdy-site.link/project_files/2c592e0a-ac16-4b41-a5a3-753cedde2d0c/822e93fe-7f15-40fb-a3ea-010140fb5a30_isman-falcon-figma2.png?v=92077e07eca8bef5c11686db6fc155dd"
            alt="ISMAN"
            className="h-12 w-auto object-contain"
          />
          <div>
            <h1 className="text-xl font-bold text-teal-800">ISMAN Engineering</h1>
            <p className="text-xs text-gray-500">Admin Portal</p>
          </div>
        </div>

        {/* Tabs */}
        {mode !== "forgot" && mode !== "update-password" && (
          <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
            <button
              onClick={() => switchMode("login")}
              className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                mode === "login"
                  ? "bg-white text-teal-700 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => switchMode("register")}
              className={`flex-1 py-2 text-sm font-semibold rounded-md transition-all cursor-pointer whitespace-nowrap ${
                mode === "register"
                  ? "bg-white text-teal-700 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Create Account
            </button>
          </div>
        )}

        {/* Title for forgot / update-password */}
        {mode === "forgot" && (
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Forgot Password?</h2>
            <p className="text-xs text-gray-500 mt-1">
              Enter your email and we&apos;ll send you a reset link.
            </p>
          </div>
        )}
        {mode === "update-password" && (
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-gray-900">Set New Password</h2>
            <p className="text-xs text-gray-500 mt-1">
              Create a strong new password for your account.
            </p>
          </div>
        )}

        {error && (
          <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
            <i className="ri-error-warning-line"></i>
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-lg px-4 py-3 flex items-center gap-2">
            <i className="ri-checkbox-circle-line"></i>
            {success}
          </div>
        )}

        {/* Login Form */}
        {mode === "login" && (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Email Address</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@isman.co.ke"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Password</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                  required
                />
              </div>
            </div>

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => switchMode("forgot")}
                className="text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
              >
                Forgot your password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold py-2.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              Sign In to Admin
            </button>
          </form>
        )}

        {/* Google Sign In button area */}
        {mode === "login" && (
          <>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-gray-400">or</span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              className="w-full bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-bold py-2.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {googleLoading && <i className="ri-loader-4-line animate-spin"></i>}
              <i className="ri-google-line text-blue-500"></i>
              Sign in with Google
            </button>

            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-blue-500 text-sm mt-0.5"></i>
                <div className="text-xs text-blue-700 space-y-1">
                  <p className="font-semibold">Need to set up Google Sign-In?</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-blue-600">
                    <li>Go to your <strong>Supabase Dashboard</strong> &rarr; Authentication &rarr; Providers</li>
                    <li>Enable <strong>Google</strong> provider</li>
                    <li>Get a <strong>Google Client ID</strong> from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer nofollow" className="underline">Google Cloud Console</a></li>
                    <li>Paste it into the <strong>Client ID</strong> field in Supabase</li>
                    <li>Add <code className="bg-blue-100 px-1 rounded">{typeof window !== "undefined" ? window.location.origin : ""}/auth/callback</code> to Google&apos;s <strong>Authorized redirect URIs</strong></li>
                    <li>Save and return here &mdash; Google Sign-In will work instantly</li>
                  </ol>
                  <p className="text-[10px] text-blue-500 mt-1">
                    After setup, signing in with <strong>ismanono@gmail.com</strong> will grant admin access automatically.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Register Form */}
        {mode === "register" && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Email Address</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@isman.co.ke"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Password</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Confirm Password</label>
              <div className="relative">
                <i className="ri-lock-password-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold py-2.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              Create Account
            </button>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === "forgot" && (
          <form onSubmit={handleForgot} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Email Address</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@isman.co.ke"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold py-2.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              Send Reset Link
            </button>

            <button
              type="button"
              onClick={() => switchMode("login")}
              className="w-full text-center text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer mt-2"
            >
              <i className="ri-arrow-left-line mr-1"></i> Back to Sign In
            </button>
          </form>
        )}

        {/* Update Password Form */}
        {mode === "update-password" && (
          <form onSubmit={handleUpdatePassword} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">New Password</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Confirm New Password</label>
              <div className="relative">
                <i className="ri-lock-password-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="&bull;&bull;&bull;&bull;&bull;&bull;"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 bg-white"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-500 text-white text-sm font-bold py-2.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {loading && <i className="ri-loader-4-line animate-spin"></i>}
              Update Password
            </button>

            <button
              type="button"
              onClick={() => { setSearchParams({}); setMode("login"); }}
              className="w-full text-center text-xs text-teal-600 hover:text-teal-700 font-medium cursor-pointer mt-2"
            >
              <i className="ri-arrow-left-line mr-1"></i> Back to Sign In
            </button>
          </form>
        )}

        {/* Footer hints */}
        {mode === "login" && (
          <p className="text-xs text-gray-400 text-center mt-6">
            Only authorized staff can access the admin panel.
          </p>
        )}
        {mode === "register" && (
          <p className="text-xs text-gray-400 text-center mt-6">
            After signing up, check your email to verify your account.
          </p>
        )}
      </div>
    </div>
  );
}
